import { useEffect, useState } from "react";
import { Check, Coins, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusPill, EmptyState } from "@/components/common";
import PaymentDialog from "@/components/PaymentDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fmtMoney, fmtDate, fmtDateTime } from "@/lib/api";

const PlanCard = ({ p, onBuy, featured }) => (
  <div className={`bg-card rounded-xl border p-6 flex flex-col fade-up ${featured ? "border-primary ring-1 ring-primary/30" : ""}`} data-testid={`plan-card-${p.id}`}>
    <div className="flex items-center justify-between"><h3 className="font-display font-bold text-lg">{p.name}</h3>{featured && <span className="text-[10px] font-mono uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">Popular</span>}</div>
    <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
    <p className="font-display text-3xl font-extrabold mt-5">{fmtMoney(p.price)}<span className="text-sm font-normal text-muted-foreground"> / {p.duration_days} days</span></p>
    <ul className="mt-5 space-y-2 text-sm flex-1">
      {p.includes_contact_access && <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />Contact access to all leads</li>}
      {(p.features || []).map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500" />{f}</li>)}
    </ul>
    <Button className="mt-6" variant={featured ? "default" : "outline"} onClick={() => onBuy(p, "plan")} data-testid={`buy-plan-${p.id}`}>Choose plan</Button>
  </div>
);

const PackageCard = ({ p, onBuy }) => (
  <div className="bg-card rounded-xl border p-6 fade-up" data-testid={`package-card-${p.id}`}>
    <div className="h-10 w-10 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center"><Coins className="h-5 w-5" /></div>
    <h3 className="font-display font-bold mt-4">{p.name}</h3>
    <p className="font-display text-3xl font-extrabold mt-2">{p.credits}<span className="text-sm font-normal text-muted-foreground"> credits</span></p>
    <p className="text-sm text-muted-foreground mt-1">{fmtMoney(p.price)} · {fmtMoney(p.price / p.credits)}/credit</p>
    <Button variant="outline" className="w-full mt-5" onClick={() => onBuy(p, "package")} data-testid={`buy-package-${p.id}`}>Buy credits</Button>
  </div>
);

const HistoryTable = ({ cols, rows, render, empty, testId }) =>
  rows.length === 0 ? <EmptyState title={empty} /> : (
    <div className="bg-card rounded-xl border overflow-x-auto"><Table data-testid={testId}>
      <TableHeader><TableRow>{cols.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
      <TableBody>{rows.map(render)}</TableBody>
    </Table></div>
  );

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [buy, setBuy] = useState(null);

  const load = () => {
    api.get("/billing/plans").then((r) => setPlans(r.data));
    api.get("/billing/packages").then((r) => setPkgs(r.data));
    api.get("/billing/payment-requests").then((r) => setReqs(r.data));
    api.get("/billing/transactions").then((r) => setTxs(r.data));
    api.get("/billing/subscriptions").then((r) => setSubs(r.data));
    refresh();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sub = user.subscription;
  return (
    <div>
      <PageHeader eyebrow="Billing" title="Plans & credits" subtitle="Pay via UPI or bank transfer. Access activates after admin verification." />
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-card rounded-xl border p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Credit balance</p><p className="font-display text-3xl font-bold mt-1" data-testid="billing-credit-balance">{user.credit_balance ?? 0}</p></div>
        <div className="bg-card rounded-xl border p-5"><p className="text-xs uppercase tracking-wider text-muted-foreground">Subscription</p>
          <p className="font-display text-xl font-bold mt-1 flex items-center gap-2" data-testid="billing-subscription-status">{sub ? <><Sparkles className="h-5 w-5 text-primary" />{sub.plan_name}</> : "No active plan"}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">Expires {fmtDate(sub.end_at)}</p>}</div>
      </div>
      <Tabs defaultValue="plans">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="plans" data-testid="tab-plans">Subscription plans</TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">Credit packages</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payment requests</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-6"><div className="grid md:grid-cols-3 gap-5">{plans.map((p, i) => <PlanCard key={p.id} p={p} featured={i === 1} onBuy={(item, kind) => setBuy({ item, kind })} />)}</div></TabsContent>
        <TabsContent value="credits" className="mt-6"><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{pkgs.map((p) => <PackageCard key={p.id} p={p} onBuy={(item, kind) => setBuy({ item, kind })} />)}</div></TabsContent>
        <TabsContent value="payments" className="mt-6">
          <HistoryTable testId="payment-requests-table" empty="No payment requests yet" cols={["Item", "Amount", "Transaction ID", "Status", "Submitted", "Admin note"]} rows={reqs}
            render={(r) => <TableRow key={r.id}><TableCell>{r.item_name}</TableCell><TableCell>{fmtMoney(r.amount)}</TableCell><TableCell className="font-mono text-xs">{r.transaction_id}</TableCell><TableCell><StatusPill status={r.status} testId={`payment-status-${r.id}`} /></TableCell><TableCell className="text-sm">{fmtDateTime(r.created_at)}</TableCell><TableCell className="text-sm text-muted-foreground">{r.admin_note || "—"}</TableCell></TableRow>} />
          {subs.length > 0 && <p className="text-xs text-muted-foreground mt-4">Subscriptions on record: {subs.map((s) => `${s.plan_name} (${fmtDate(s.start_at)} → ${fmtDate(s.end_at)}, ${s.status})`).join(" · ")}</p>}
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <HistoryTable testId="transactions-table" empty="No credit transactions yet" cols={["Date", "Type", "Amount", "Balance after", "Note"]} rows={txs}
            render={(t) => <TableRow key={t.id}><TableCell className="text-sm">{fmtDateTime(t.created_at)}</TableCell><TableCell className="font-mono text-xs uppercase">{t.type}</TableCell><TableCell className={`font-mono ${t.amount > 0 ? "text-emerald-600" : "text-rose-600"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</TableCell><TableCell className="font-mono">{t.balance_after}</TableCell><TableCell className="text-sm text-muted-foreground">{t.note}</TableCell></TableRow>} />
        </TabsContent>
      </Tabs>
      <PaymentDialog open={!!buy} item={buy?.item} kind={buy?.kind} onOpenChange={(o) => !o && setBuy(null)} onDone={load} />
    </div>
  );
}
